import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Collapse } from 'react-bootstrap';
import { getItem } from '../../helper/localstorage.helper';
import Sidebar from '../../components/Sidebar';
import { Header } from '../../components/header';
import '../../scss/faq-dashboard.scss';
import { faqData, faqData3 } from '../../constant/faq-question.constant'

type UnifiedFAQ = {
    id?: string;
    question?: string;
    answer?: string;
    title?: string;
    description?: string;
    category?: 'general' | 'login' | 'features' | 'pension';
    para1?: string;
    para2?: string;
    para3?: string;
    para4?: string;
    para5?: string;
    para6?: string;
    para7?: string;
    para8?: string;
    para9?: string;
    para10?: string;
    li1?: string[];
    li2?: string[];
    heading?: string;
    heading1?: string;
    heading2?: string;
    head?: string;
    subDescription?: string;
    supportEmail?: string;
    supportEmail1?: string;
    supportEmail2?: string;
    support?: string;
    support1?: string;
    support2?: string;
    link?: string;
    redirect?: string;
    span?: string;
};


export const FaqDashboardPage: React.FC = () => {
    const { t } = useTranslation();
    const [searchQuery, setSearchQuery] = useState('');
    const [openItems, setOpenItems] = useState<string[]>([]);
    const isLoggedIn = JSON.parse(getItem('user') || 'null');
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    
    // Transform faqData3 and faqPension to match the structure
    const transformedFaqData3 = faqData3.map((item, index) => ({
        id: `ext-${index + 1}`,
        question: item.title,
        answer: item.description,
        category: 'general' as const,
        ...item
    }));
    
    // const transformedFaqPension = faqPension.map((item, index) => ({
    //     id: `pension-${index + 1}`,
    //     question: item.title,
    //     answer: item.description,
    //     category: 'pension' as const,
    //     ...item
    // }));
    
    const allFAQs = [...faqData, ...transformedFaqData3] as UnifiedFAQ[];
    // Filter by search query
    const filteredFAQs = searchQuery.trim() === '' ? allFAQs : allFAQs.filter(faq => {
        const searchTerm = searchQuery.toLowerCase();
        const keywords = searchTerm.split(' ').filter(word => word.length > 0);

        const containsAllKeywords = (text: string) => {
            const lowerText = text.toLowerCase();
            return keywords.every(keyword => lowerText.includes(keyword));
        };

        if ((faq.question && containsAllKeywords(t(faq.question))) || (faq.answer && containsAllKeywords(t(faq.answer)))) {
            return true;
        }

        const paragraphs = [faq.para1, faq.para2, faq.para3, faq.para4, faq.para5, faq.para6, faq.para7, faq.para8, faq.para9, faq.subDescription];
        for (const para of paragraphs) {
            if (para && typeof para === 'string' && containsAllKeywords(t(para))) {
                return true;
            }
        }

        const headings = [faq.heading1, faq.heading2, faq.heading, faq.head];
        for (const heading of headings) {
            if (heading && containsAllKeywords(t(heading))) {
                return true;
            }
        }

        const lists = [faq.li1, faq.li2];
        for (const list of lists) {
            if (list) {
                for (const item of list) {
                    if (containsAllKeywords(t(item))) {
                        return true;
                    }
                }
            }
        }

        return false;
    });

    const toggleItem = (id: string) => {
        setOpenItems(prev =>
            prev.includes(id)
                ? prev.filter(item => item !== id)
                : [id]
        );
    };

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();
    };

    return (
        <div className="dashboard-page">
            <div className="dashboard-layout">
                <Sidebar activeItemId='faq-help'
                footer={null}
                onClose={() => setIsSidebarOpen(false)}
                className={`dashboard-sidebar ${isSidebarOpen ? 'sidebar-open' : ''}`}
                />

                <div className="dashboard-main-content">
                    <Header 
                    setIsSidebarOpen={setIsSidebarOpen}
                    />

                    <div className="px-3">
                        <div className="faq-dashboard-page">
                            <div className="faq-dashboard-header">
                                <h1 className="faq-dashboard-title">{t('SIDEBAR_FAQ_HELP')}</h1>

                                <div className="faq-search-wrapper">
                                    <div className="faq-search-input">
                                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="search-icon">
                                            <path d="M7.33333 12.6667C10.2789 12.6667 12.6667 10.2789 12.6667 7.33333C12.6667 4.38781 10.2789 2 7.33333 2C4.38781 2 2 4.38781 2 7.33333C2 10.2789 4.38781 12.6667 7.33333 12.6667Z" stroke="#68727D" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                            <path d="M14 14L11.1 11.1" stroke="#68727D" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                        <input
                                            type="text"
                                            placeholder={t('SEARCH')}
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                            className="search-field"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="faq-dashboard-content">
                                <h2 className="common-questions-title">
                                    {t('FAQ_COMMON_QUESTIONS')}
                                </h2>

                                <div className="faq-items-list">
                                    {filteredFAQs.map((faq, index) => {
                                        const faqId = faq.id || `faq-${index}`;
                                        const faqQuestion = faq.question || '';
                                        const faqAnswer = faq.answer || '';
                                        
                                        return (
                                        <div key={faqId} className="faq-item-dashboard">
                                            <div
                                                className="faq-item-header-dashboard"
                                                onClick={() => toggleItem(faqId)}
                                                role="button"
                                                tabIndex={0}
                                                onKeyPress={(e) => e.key === 'Enter' && toggleItem(faqId)}
                                            >
                                                <h3 className="faq-question">
                                                    {t(faqQuestion)}
                                                </h3>
                                                <div className={`faq-chevron-icon ${openItems.includes(faqId) ? 'rotated' : ''}`}>
                                                    <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                                                        <circle cx="16" cy="16" r="15.5" stroke="#252B37" />
                                                        <path
                                                            d="M12 14L16 18L20 14"
                                                            stroke="#252B37"
                                                            strokeWidth="2"
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                        />
                                                    </svg>
                                                </div>
                                            </div>

                                            <Collapse in={openItems.includes(faqId)}>
                                                <div className="faq-item-answer">
                                                    <div className="faq-answer-content">
                                                        <p style={{ whiteSpace: 'pre-line' }}>
                                                            {t(faqAnswer)}
                                                        </p>
                                                        {faq.subDescription && <p>{t(faq.subDescription)}</p>}
                                                        {faq.heading1 && <h6 className="fw-bold">{t(faq.heading1)}</h6>}
                                                        {faq.para1 && typeof faq.para1 === 'string' && <p>{t(faq.para1)}</p>}
                                                        {faq.supportEmail && (
                                                            <a href={faq.support} className="text-decoration-underline d-inline-block mb-2">
                                                                {faq.supportEmail}
                                                            </a>
                                                        )}
                                                        {faq.para2 && typeof faq.para2 === 'string' && <p>{t(faq.para2)}</p>}
                                                        {faq.para3 && typeof faq.para3 === 'string' && <p>{t(faq.para3)}</p>}
                                                        {faq.li1 && (
                                                            <ul>
                                                                {faq.li1.map((item, liIndex) => (
                                                                    <li key={liIndex}>{t(item)}</li>
                                                                ))}
                                                            </ul>
                                                        )}
                                                        {faq.heading2 && <h6 className="fw-bold">{t(faq.heading2)}</h6>}
                                                        {faq.para4 && typeof faq.para4 === 'string' && <p>{t(faq.para4)}</p>}
                                                        {faq.para5 && typeof faq.para5 === 'string' && <p>{t(faq.para5)}</p>}
                                                        {faq.para6 && typeof faq.para6 === 'string' && <p>{t(faq.para6)}</p>}
                                                        {faq.para7 && typeof faq.para7 === 'string' && <p>{t(faq.para7)}</p>}
                                                        {faq.para8 && typeof faq.para8 === 'string' && <p>{t(faq.para8)}</p>}
                                                        {faq.supportEmail2 && (
                                                            <a href={faq.support2} className="text-decoration-underline d-inline-block mb-2">
                                                                {faq.supportEmail2}
                                                            </a>
                                                        )}
                                                        {faq.para9 && typeof faq.para9 === 'string' && <p>{t(faq.para9)}</p>}
                                                        {faq.link && !(faq.id === '58' && !isLoggedIn) && (
                                                            <a href={faq.redirect} target="_blank" rel="noopener noreferrer" className="text-decoration-underline d-inline-block mb-2">
                                                                {t(faq.link)}
                                                            </a>
                                                        )}
                                                        {faq.head && <h6 className="fw-bold">{t(faq.head)}</h6>}
                                                        {faq.li2 && (
                                                            <ul>
                                                                {faq.li2.map((item, liIndex) => (
                                                                    <li key={liIndex}>{t(item)}</li>
                                                                ))}
                                                            </ul>
                                                        )}
                                                        {faq.supportEmail1 && (
                                                            <a href={faq.support1} className="text-decoration-underline d-inline-block">
                                                                {faq.supportEmail1}
                                                            </a>
                                                        )}
                                                    </div>
                                                </div>
                                            </Collapse>
                                        </div>
                                        );
                                    })}
                                </div>

                                {searchQuery.trim() !== '' && filteredFAQs.length === 0 && (
                                    <div className="no-results">
                                        <p>{t('NO_SEARCH_RESULTS')}</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FaqDashboardPage;
